import { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import {
  FiMail,
  FiCheckCircle,
  FiAlertCircle,
  FiInfo,
  FiLock,
  FiEye,
  FiEyeOff,
} from "react-icons/fi";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

// Ensure URL always has a protocol so fetch() doesn't interpret it as a relative path
const formatApiUrl = (url) => {
  if (!url) return "http://localhost:5000";
  if (url.startsWith("http")) return url;
  return url.includes("localhost") ? `http://${url}` : `https://${url}`;
};

const API_BASE_URL = formatApiUrl(process.env.REACT_APP_API_HOST);

const Box = styled.div`
  width: min(900px, 95vw);
  height: 560px;
  border-radius: 24px;
  position: relative;
  overflow: hidden;

  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.08);

  .text-link {
    color: #eb5e28;
    cursor: pointer;
    font-weight: 600;
    font-size: 0.95rem;
    margin-top: 16px;
    display: inline-block;
    transition: color 0.2s;
    text-decoration: none;

    &:hover {
      color: #c44a1d;
    }
  }

  .text-link-gray {
    color: #6b7280;
    font-weight: 500;

    &:hover {
      color: #eb5e28;
    }
  }

  .panel {
    position: absolute;
    top: 0;
    height: 100%;
    transition: left 0.7s cubic-bezier(0.65, 0, 0.35, 1);
  }
  .pA {
    left: 0;
    width: 55%;
    z-index: 1;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  &.signup-mode .pA {
    left: 45%;
  }
  .pB {
    left: 55%;
    width: 45%;
    z-index: 2;
    background: linear-gradient(
      135deg,
      rgba(255, 250, 246, 0.95),
      rgba(255, 242, 235, 0.98)
    );
    backdrop-filter: blur(16px);
    -webkit-backdrop-filter: blur(16px);
    border-left: 1px solid rgba(0, 0, 0, 0.05);
    border-right: 1px solid rgba(0, 0, 0, 0);
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
      left 0.7s cubic-bezier(0.65, 0, 0.35, 1),
      border-color 0.7s;
  }
  &.signup-mode .pB {
    left: 0;
    border-left-color: rgba(0, 0, 0, 0);
    border-right-color: rgba(255, 255, 255, 0.5);
  }
  .side-content {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition:
      opacity 0.4s ease,
      visibility 0.4s;
  }
  .new-here {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
  .welcome-back {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
  &.signup-mode .new-here {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
  &.signup-mode .welcome-back {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
  .pC {
    position: absolute;
    top: 0;
    left: 100%;
    width: 100%;
    height: 100%;
    z-index: 3;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    transition: left 0.7s cubic-bezier(0.65, 0, 0.35, 1);
    padding: 40px;
    text-align: center;
  }
  &.check-inbox-mode .pC {
    left: 0;
  }
  .icon-circle {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: rgba(235, 94, 40, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    color: #eb5e28;
  }
  .pC h2 {
    margin-bottom: 12px;
    font-size: 2rem;
    color: #202124;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
  .pC p {
    color: #5f6368;
    font-size: 1.05rem;
    line-height: 1.5;
    max-width: 400px;
    margin-bottom: 32px;
  }
  @media (max-width: 768px) {
    height: auto;
    flex-direction: column;
    overflow: hidden;
    .panel {
      position: relative;
      left: 0 !important;
      width: 100% !important;
      height: auto;
      transition: none !important;
    }
    .pB {
      display: none;
    }
    .pC {
      position: absolute;
      top: 0;
      left: 100% !important;
      height: 100%;
      border-radius: 24px;
      transition: left 0.7s cubic-bezier(0.65, 0, 0.35, 1) !important;
    }
    &.check-inbox-mode .pC {
      left: 0 !important;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 14px;
  width: 80%;
  max-width: 360px;
  padding: 40px 0;
  h2 {
    margin-bottom: 6px;
    font-size: 1.8rem;
    color: #202124;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
  p.subtext {
    color: #5f6368;
    margin-top: -6px;
    margin-bottom: 20px;
    font-size: 0.95rem;
    line-height: 1.5;
  }

  input {
    padding: 13px 16px;
    border-radius: 8px;
    border: 1px solid #dadce0;
    background: #ffffff;
    font-family: inherit;
    font-size: 0.95rem;
    color: #202124;
    transition: all 0.2s ease;
    outline: none;
  }

  /* Apply red borders dynamically if the error-input class is present */
  input.error-input {
    border-color: #ef4444;
  }
  input.error-input:focus {
    box-shadow: 0 0 0 1px #ef4444;
  }

  input:focus:not(.error-input) {
    border-color: #eb5e28;
    box-shadow: 0 0 0 1px #eb5e28;
  }

  input::placeholder {
    color: #80868b;
  }
  button {
    margin-top: 20px;
    width: 100%;
  }

  @media (max-width: 768px) {
    min-height: 420px;
    padding: 32px 0 40px 0;
    width: 90%;
  }
`;

const Side = styled.div`
  text-align: center;
  padding: 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  h3 {
    font-size: 2rem;
    color: #202124;
    font-weight: 600;
    letter-spacing: -0.01em;
    margin: 0;
  }
  p {
    color: #5f6368;
    font-size: 1rem;
    line-height: 1.5;
    margin-bottom: 12px;
  }
  button {
    background: transparent !important;
    color: #eb5e28 !important;
    border: 1px solid #eb5e28 !important;
    padding: 12px 32px;
    border-radius: 25px;
    font-weight: bold;
    font-size: 0.9rem;
    letter-spacing: 0.14em;
    cursor: pointer;
    text-shadow: none;
  }
  button::before {
    display: none;
  }
  button:hover {
    background: rgba(235, 94, 40, 0.04) !important;
    box-shadow: none !important;
  }
`;

const MobileNav = styled.div`
  display: none;
  transition:
    opacity 0.4s ease,
    visibility 0.4s;
  &.hidden {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
  @media (max-width: 768px) {
    display: flex;
    background: rgba(0, 0, 0, 0.03);
    border-radius: 16px;
    padding: 6px;
    margin: 24px 24px 0 24px;
    gap: 8px;
    position: relative;
    z-index: 10;
  }
`;

const Tab = styled.div`
  flex: 1;
  padding: 12px;
  text-align: center;
  font-size: 0.9rem;
  font-weight: 600;
  border-radius: 12px;
  cursor: pointer;
  background: ${({ active }) => (active ? "#ffffff" : "transparent")};
  color: ${({ active }) => (active ? "#202124" : "#5f6368")};
  box-shadow: ${({ active }) =>
    active ? "0 2px 8px rgba(0,0,0,0.06)" : "none"};
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
`;

const InputGroup = styled.div`
  display: flex;
  gap: 12px;
  input {
    width: 100%;
  }
`;

const TrustNote = styled.p`
  font-size: 0.78rem;
  color: #7b746c;
  margin: 0;
  text-align: center;
  letter-spacing: 0.01em;
  a {
    color: #eb5e28;
    text-decoration: none;
    transition: color 0.2s;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const FormSlider = styled.div`
  display: grid;
  grid-template-columns: 100%;
  grid-template-areas: "form";
  width: 100%;
  overflow: hidden;
  .form-slide {
    grid-area: form;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    transition:
      transform 0.5s cubic-bezier(0.65, 0, 0.35, 1),
      opacity 0.3s ease,
      visibility 0.5s;
  }
  .slide-signin {
    transform: translateX(0);
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
  }
  .slide-signup {
    transform: translateX(100%);
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
  }
  .slide-forgot {
    transform: translateX(-100%);
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
  }
  &.signup-active .slide-signin {
    transform: translateX(-100%);
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
  }
  &.signup-active .slide-signup {
    transform: translateX(0);
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
  }
  &.signup-active .slide-forgot {
    transform: translateX(-200%);
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
  }
  &.forgot-active .slide-signin {
    transform: translateX(100%);
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
  }
  &.forgot-active .slide-signup {
    transform: translateX(200%);
    opacity: 0;
    pointer-events: none;
    visibility: hidden;
  }
  &.forgot-active .slide-forgot {
    transform: translateX(0);
    opacity: 1;
    pointer-events: auto;
    visibility: visible;
  }
  @media (min-width: 769px) {
    .form-slide {
      align-items: center;
      transition:
        transform 0.5s cubic-bezier(0.65, 0, 0.35, 1),
        opacity 0.4s ease,
        visibility 0.4s;
    }
    .slide-signup {
      transform: translateX(0) !important;
    }
    &.signup-active .slide-signin {
      transform: translateX(0) !important;
    }
  }
`;

const ResetPanel = styled.div`
  position: absolute;
  top: 0;
  left: 100%;
  width: 100%;
  height: 100%;
  z-index: 4;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: left 0.7s cubic-bezier(0.65, 0, 0.35, 1);
  &.active {
    left: 0;
  }
  .reset-view {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 40px;
    text-align: center;
    transition:
      opacity 0.4s ease,
      visibility 0.4s;
  }
  .reset-form-view {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
  .reset-success-view {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
  &.done .reset-form-view {
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
  }
  &.done .reset-success-view {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
  @media (max-width: 768px) {
    left: 100% !important;
    border-radius: 24px;
    &.active {
      left: 0 !important;
    }
  }
`;

const PasswordInputWrap = styled.div`
  position: relative;
  width: 100%;
  input {
    width: 100%;
    padding: 13px 42px 13px 16px;
    border-radius: 8px;
    border: 1px solid ${({ $error }) => ($error ? "#ef4444" : "#dadce0")};
    background: #ffffff;
    font-family: inherit;
    font-size: 0.95rem;
    color: #202124;
    transition: all 0.2s ease;
    outline: none;
    box-sizing: border-box;
  }
  input:focus {
    border-color: ${({ $error }) => ($error ? "#ef4444" : "#eb5e28")};
    box-shadow: 0 0 0 1px ${({ $error }) => ($error ? "#ef4444" : "#eb5e28")};
  }
  input.error-input {
    border-color: #ef4444;
  }
  input.error-input:focus {
    box-shadow: 0 0 0 1px #ef4444;
  }
  input::placeholder {
    color: #80868b;
  }
  .eye {
    position: absolute;
    right: 13px;
    top: 50%;
    transform: translateY(-50%);
    cursor: pointer;
    color: #9ca3af;
    display: flex;
    transition: color 0.2s;
    &:hover {
      color: #eb5e28;
    }
  }
`;

const ErrorHint = styled.span`
  font-size: 0.8rem;
  color: #ef4444;
  text-align: left;
  margin-top: -4px;
  width: 100%;
  display: block;
`;

// Local Validations
const isStrongPassword = (pass) => {
  return (
    pass.length >= 8 &&
    /[a-z]/.test(pass) &&
    /[A-Z]/.test(pass) &&
    /[0-9]/.test(pass) &&
    /[^a-zA-Z0-9]/.test(pass)
  );
};

const BetaClosedBox = styled.div`
  width: min(560px, 95vw);
  min-height: 360px;
  border-radius: 24px;
  position: relative;
  overflow: hidden;

  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.8);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.08);

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
  text-align: center;

  .icon-circle {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: rgba(235, 94, 40, 0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 24px;
    color: #eb5e28;
  }

  h2 {
    margin-bottom: 12px;
    font-size: 2rem;
    color: #202124;
    font-weight: 600;
    letter-spacing: -0.01em;
  }

  p {
    color: #5f6368;
    font-size: 1.05rem;
    line-height: 1.6;
    max-width: 420px;
    margin: 0 0 24px;
  }

  a {
    color: #eb5e28;
    font-weight: 600;
    font-size: 1.05rem;
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
      color: #c44a1d;
    }
  }

  @media (max-width: 768px) {
    padding: 40px 24px;
    min-height: 320px;

    h2 {
      font-size: 1.6rem;
    }
  }
`;

const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

function AuthPanel({
  signup = false,
  verify = false,
  resetToken = null,
  emailConfirm = false,
  confirmUserId = null,
  confirmToken = null,
  onResetClose = () => {},
}) {
  const [authMode, setAuthMode] = useState(signup ? "signup" : "signin");
  const [checkInboxType, setCheckInboxType] = useState(null);
  const [apiErrorMsg, setApiErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();

  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [signupForm, setSignupForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    inviteCode: "",
  });
  const [forgotEmail, setForgotEmail] = useState("");

  const [signupError, setSignupError] = useState("");
  const [loginError, setLoginError] = useState("");
  const [forgotError, setForgotError] = useState("");

  const [isResetDismissed, setIsResetDismissed] = useState(false);
  const [resetPass, setResetPass] = useState("");
  const [resetConfirm, setResetConfirm] = useState("");
  const [showResetPass, setShowResetPass] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  const showResetPanel =
    resetToken && !isResetDismissed && !verify && !emailConfirm;

  const [emailChangeSuccess, setEmailChangeSuccess] = useState(false);

  // Determine which fields to highlight red in the signup form
  const isSignupEmailError =
    signupError.toLowerCase().includes("email") ||
    signupError.toLowerCase().includes("user") ||
    signupError.toLowerCase().includes("exist") ||
    signupError.toLowerCase().includes("valid");
  const isSignupPasswordError = signupError.toLowerCase().includes("password");

  // Track if verification was already attempted to avoid React 18 Strict Mode double-firing
  const verifyAttempted = useRef(false);
  const emailConfirmAttempted = useRef(false);

  useEffect(() => {
    const invite = searchParams.get("invite");
    if (invite) {
      setSignupForm((prev) => ({ ...prev, inviteCode: invite }));
    }
  }, [searchParams]);

  useEffect(() => {
    if (verify && resetToken && !verifyAttempted.current) {
      verifyAttempted.current = true; // Lock gate immediately

      setCheckInboxType(null);
      const verifyEmail = async () => {
        try {
          const response = await fetch(`${API_BASE_URL}/auth/verify`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: resetToken }),
          });

          if (response.status === 429) {
            setCheckInboxType("error");
            setApiErrorMsg("Too many requests. Please try again later.");
            return;
          }

          const data = await response.json();
          if (response.ok && data.success) {
            setCheckInboxType("verified");
          } else {
            setCheckInboxType("error");
            setApiErrorMsg(
              data.message || "Invalid or expired verification link.",
            );
          }
        } catch (err) {
          setCheckInboxType("error");
          setApiErrorMsg("Unable to connect to the server.");
        }
      };
      verifyEmail();
    }
  }, [verify, resetToken]);

  useEffect(() => {
    if (
      !emailConfirm ||
      !confirmUserId ||
      !confirmToken ||
      emailConfirmAttempted.current
    ) {
      return;
    }
    emailConfirmAttempted.current = true;
    setCheckInboxType(null);
    setEmailChangeSuccess(false);

    const confirmEmailChange = async () => {
      try {
        const pathId = encodeURIComponent(confirmUserId);
        const pathToken = encodeURIComponent(confirmToken);
        const response = await fetch(
          `${API_BASE_URL}/auth/email/${pathId}/${pathToken}`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
          },
        );

        if (response.status === 429) {
          setCheckInboxType("error");
          setApiErrorMsg("Too many requests. Please try again later.");
          return;
        }

        const data = await response.json();
        if (response.ok && data.success) {
          setEmailChangeSuccess(true);
          setCheckInboxType("verified");
        } else {
          setCheckInboxType("error");
          setApiErrorMsg(
            data.message || "Invalid or expired confirmation link.",
          );
        }
      } catch (err) {
        setCheckInboxType("error");
        setApiErrorMsg("Unable to connect to the server.");
      }
    };
    confirmEmailChange();
  }, [emailConfirm, confirmUserId, confirmToken]);

  const handleSignInSubmit = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...loginForm,
          email: loginForm.email.trim(),
        }),
      });

      // Intercept plain text errors before parsing JSON
      if (response.status === 401) {
        setLoginError("Invalid email or password.");
        setLoading(false);
        return;
      }
      if (response.status === 429) {
        setLoginError("Too many login attempts. Please try again later.");
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (response.ok && data.success) {
        login(data.token, data.user);
        if (data.user?.is_affiliate) {
          if (!data.user.affiliate_setup_complete && data.user.affiliate_team_id) {
            window.location.href = "/app/affiliate/setup";
          } else {
            window.location.href = "/app/affiliate";
          }
        } else {
          window.location.href = "/app";
        }
      } else if (
        response.status === 403 &&
        data.message?.toLowerCase().includes("verify")
      ) {
        setCheckInboxType("unverified");
      } else if (response.status >= 400 && response.status < 500) {
        setLoginError(data.message || "Invalid email or password.");
      } else {
        setCheckInboxType("error");
        setApiErrorMsg(data.message || "Something went wrong on our end.");
      }
    } catch (err) {
      setCheckInboxType("error");
      setApiErrorMsg(
        "Unable to connect to the server. Please check your connection.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSignUpSubmit = async (e) => {
    e.preventDefault();
    setSignupError("");

    if (!isValidEmail(signupForm.email)) {
      setSignupError("Please enter a valid email address.");
      return;
    }

    if (!isStrongPassword(signupForm.password)) {
      setSignupError(
        "Password must contain at least 8 chars, 1 uppercase, 1 lowercase, 1 number, and 1 symbol.",
      );
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...signupForm,
          email: signupForm.email.trim(),
        }),
      });

      if (response.status === 429) {
        setSignupError("Too many requests. Please try again later.");
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (response.ok && data.success) {
        setCheckInboxType("signup");
      } else if (response.status >= 400 && response.status < 500) {
        setSignupError(
          data.message || "Registration failed. Please check your details.",
        );
      } else {
        setCheckInboxType("error");
        setApiErrorMsg("Something went wrong on our end.");
      }
    } catch (err) {
      setCheckInboxType("error");
      setApiErrorMsg(
        "Unable to connect to the server. Please check your connection.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotError("");

    if (!isValidEmail(forgotEmail)) {
      setForgotError("Please enter a valid email address.");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/auth/password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: forgotEmail.trim() }),
      });

      if (response.ok) {
        setCheckInboxType("forgot");
      } else {
        const data = await response.json().catch(() => ({}));
        setForgotError(data.message || "Failed to initiate password reset.");
      }
    } catch (err) {
      setForgotError("Unable to connect to the server.");
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setResetError("");

    if (!isStrongPassword(resetPass)) {
      setResetError(
        "Password must contain at least 8 chars, 1 uppercase, 1 lowercase, 1 number, and 1 symbol.",
      );
      return;
    }
    if (resetPass !== resetConfirm) {
      setResetError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const [id, jwtToken] = resetToken.split("/");
      const response = await fetch(
        `${API_BASE_URL}/auth/password/${id}/${jwtToken}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ newPassword: resetPass }),
        },
      );

      if (response.status === 429) {
        setResetError("Too many requests. Please try again later.");
        setLoading(false);
        return;
      }

      const data = await response.json();

      if (response.ok && data.success) {
        setResetSuccess(true);
      } else {
        setResetError(data.message || "Invalid or expired reset link.");
      }
    } catch (err) {
      setResetError("Connection error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const showBetaClosed =
    !verify && !emailConfirm && !showResetPanel && !checkInboxType;

  if (showBetaClosed) {
    return (
      <BetaClosedBox>
        <div className="icon-circle">
          <FiInfo size={32} />
        </div>
        <h2>This beta has ended</h2>
        <p>
          Thanks for trying Smarcuit and sharing your feedback. We're no longer
          accepting sign-ups through the beta. To learn more about getting
          Smarcuit, contact{" "}
          <a href="mailto:sales@smarcu.it">sales@smarcu.it</a>.
        </p>
      </BetaClosedBox>
    );
  }

  return (
    <Box
      className={`${authMode === "signup" ? "signup-mode" : ""} ${checkInboxType ? "check-inbox-mode" : ""} ${showResetPanel ? "reset-mode" : ""}`}
    >
      <MobileNav className={checkInboxType || showResetPanel ? "hidden" : ""}>
        <Tab
          active={authMode !== "signup"}
          onClick={() => setAuthMode("signin")}
        >
          Sign In
        </Tab>
        <Tab
          active={authMode === "signup"}
          onClick={() => setAuthMode("signup")}
        >
          Sign Up
        </Tab>
      </MobileNav>

      <div className={`panel pA ${authMode === "signup" ? "show-reg" : ""}`}>
        <FormSlider
          className={`${authMode === "signup" ? "signup-active" : ""} ${authMode === "forgot" ? "forgot-active" : ""}`}
        >
          {/* FORGOT PASSWORD SLIDE */}
          <div className="form-slide slide-forgot">
            <Form onSubmit={handleForgotSubmit}>
              <h2 style={{ textAlign: "center" }}>Reset Password</h2>
              <p className="subtext" style={{ textAlign: "center" }}>
                Enter your email address and we'll send you a link to reset your
                password.
              </p>
              <input
                placeholder="Email"
                type="email"
                className={forgotError ? "error-input" : ""}
                required
                value={forgotEmail}
                onChange={(e) => {
                  setForgotEmail(e.target.value);
                  setForgotError("");
                }}
              />
              {forgotError && (
                <ErrorHint style={{ marginTop: "-10px", marginBottom: "0px" }}>
                  {forgotError}
                </ErrorHint>
              )}
              <button type="submit" disabled={loading}>
                {loading ? "Sending..." : "Send Reset Link"}
              </button>
              <div style={{ textAlign: "center", marginTop: "16px" }}>
                <span
                  className="text-link text-link-gray"
                  onClick={() => setAuthMode("signin")}
                >
                  Back to Sign In
                </span>
              </div>
            </Form>
          </div>

          {/* SIGN IN SLIDE */}
          <div className="form-slide slide-signin">
            <Form onSubmit={handleSignInSubmit}>
              <h2 style={{ textAlign: "center" }}>Welcome Back</h2>
              <p className="subtext" style={{ textAlign: "center" }}>
                Enter your details to access your workspace.
              </p>
              <input
                placeholder="Email"
                type="email"
                className={loginError ? "error-input" : ""}
                required
                value={loginForm.email}
                onChange={(e) => {
                  setLoginForm({ ...loginForm, email: e.target.value });
                  setLoginError("");
                }}
              />
              <PasswordInputWrap $error={!!loginError}>
                <input
                  placeholder="Password"
                  type="password"
                  className={loginError ? "error-input" : ""}
                  required
                  value={loginForm.password}
                  onChange={(e) => {
                    setLoginForm({ ...loginForm, password: e.target.value });
                    setLoginError("");
                  }}
                />
              </PasswordInputWrap>
              <div
                style={{
                  textAlign: "right",
                  marginTop: "-10px",
                  marginBottom: "4px",
                }}
              >
                <span
                  className="text-link text-link-gray"
                  style={{ fontSize: "0.85rem", marginTop: 0 }}
                  onClick={() => setAuthMode("forgot")}
                >
                  Forgot Password?
                </span>
              </div>
              {loginError && (
                <ErrorHint style={{ marginTop: "-4px" }}>
                  {loginError}
                </ErrorHint>
              )}
              <button type="submit" disabled={loading}>
                {loading ? "Signing In..." : "Sign In"}
              </button>
            </Form>
          </div>

          {/* SIGN UP SLIDE */}
          <div className="form-slide slide-signup">
            <Form onSubmit={handleSignUpSubmit}>
              <h2>Create Account</h2>
              <p className="subtext">
                Join Smarcuit to build your scalable talent network.
              </p>
              <InputGroup>
                <input
                  placeholder="First Name"
                  required
                  value={signupForm.firstName}
                  onChange={(e) => {
                    setSignupForm({ ...signupForm, firstName: e.target.value });
                    setSignupError("");
                  }}
                />
                <input
                  placeholder="Last Name"
                  required
                  value={signupForm.lastName}
                  onChange={(e) => {
                    setSignupForm({ ...signupForm, lastName: e.target.value });
                    setSignupError("");
                  }}
                />
              </InputGroup>

              <input
                placeholder="Email"
                type="email"
                className={isSignupEmailError ? "error-input" : ""}
                required
                value={signupForm.email}
                onChange={(e) => {
                  setSignupForm({ ...signupForm, email: e.target.value });
                  setSignupError("");
                }}
              />
              {isSignupEmailError && (
                <ErrorHint style={{ marginTop: "-10px", marginBottom: "0px" }}>
                  {signupError}
                </ErrorHint>
              )}

              <PasswordInputWrap $error={isSignupPasswordError}>
                <input
                  placeholder="Password"
                  type="password"
                  className={isSignupPasswordError ? "error-input" : ""}
                  required
                  value={signupForm.password}
                  onChange={(e) => {
                    setSignupForm({ ...signupForm, password: e.target.value });
                    setSignupError("");
                  }}
                />
              </PasswordInputWrap>
              {!isSignupEmailError && signupError && (
                <ErrorHint style={{ marginTop: "-10px" }}>
                  {signupError}
                </ErrorHint>
              )}

              <input
                placeholder="Invite Code (Optional)"
                type="text"
                value={signupForm.inviteCode}
                onChange={(e) =>
                  setSignupForm({ ...signupForm, inviteCode: e.target.value })
                }
              />

              <button type="submit" disabled={loading}>
                {loading ? "Creating..." : "Get Started"}
              </button>
              <TrustNote>
                By signing up, you agree to our{" "}
                <a href="/terms" target="_blank" rel="noopener noreferrer">
                  Terms
                </a>{" "}
                and{" "}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                .
              </TrustNote>
            </Form>
          </div>
        </FormSlider>
      </div>

      <div
        className={`panel pB ${authMode === "signup" ? "show-welcome" : ""}`}
      >
        <div className="side-content new-here">
          <Side>
            <h3>New Here?</h3>
            <p>
              Discover next-generation people ops and transform hiring into a
              data-informed science.
            </p>
            <button onClick={() => setAuthMode("signup")}>
              Create an Account
            </button>
          </Side>
        </div>
        <div className="side-content welcome-back">
          <Side>
            <h3>Welcome Back</h3>
            <p>
              Already have a workspace setup? Sign in to continue building your
              team.
            </p>
            <button onClick={() => setAuthMode("signin")}>Sign In</button>
          </Side>
        </div>
      </div>

      <div className="pC">
        <div
          className="icon-circle"
          style={
            checkInboxType === "verified"
              ? { color: "#10b981", background: "rgba(16, 185, 129, 0.08)" }
              : checkInboxType === "error"
                ? { color: "#ef4444", background: "rgba(239, 68, 68, 0.08)" }
                : { color: "#eb5e28", background: "rgba(235, 94, 40, 0.08)" }
          }
        >
          {checkInboxType === "verified" ? (
            <FiCheckCircle size={32} />
          ) : checkInboxType === "error" ? (
            <FiAlertCircle size={32} />
          ) : (
            <FiMail size={32} />
          )}
        </div>
        <h2>
          {checkInboxType === "verified"
            ? "Email Verified"
            : checkInboxType === "error"
              ? "Action Failed"
              : checkInboxType === "unverified"
                ? "Verification Required"
                : "Check Your Inbox"}
        </h2>
        <p>
          {checkInboxType === "verified"
            ? emailChangeSuccess
              ? "Your email address has been updated. Sign in with your new email to access your workspace."
              : "Your email has been successfully verified. You can now sign in to your workspace."
            : checkInboxType === "error"
              ? apiErrorMsg
              : checkInboxType === "forgot"
                ? "We've sent a password reset link to your email address. Click the link to choose a new password."
                : checkInboxType === "unverified"
                  ? "Please verify your email address before logging in. Check your inbox for the verification link."
                  : "We've sent a magic link to your email address. Click the link to verify your account."}
        </p>
        <span
          className="text-link"
          onClick={() => {
            if (checkInboxType === "verified") window.location.href = "/login";
            setCheckInboxType(null);
            if (checkInboxType !== "error") setAuthMode("signin");
          }}
        >
          {checkInboxType === "verified"
            ? "Continue to Workspace"
            : checkInboxType === "error"
              ? "Try Again"
              : "Back to Sign In"}
        </span>
      </div>

      <ResetPanel
        className={`${showResetPanel ? "active" : ""} ${resetSuccess ? "done" : ""}`}
      >
        <div className="reset-view reset-form-view">
          <div className="icon-circle">
            <FiLock size={32} />
          </div>
          <h2>Set New Password</h2>
          <p>Choose a strong password for your Smarcuit workspace.</p>
          <form
            onSubmit={handleResetSubmit}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 12,
              width: "100%",
              maxWidth: 320,
              marginTop: 4,
            }}
          >
            <PasswordInputWrap
              $error={
                resetError &&
                resetPass.length > 0 &&
                !isStrongPassword(resetPass)
              }
            >
              <input
                type={showResetPass ? "text" : "password"}
                placeholder="New Password"
                value={resetPass}
                onChange={(e) => {
                  setResetPass(e.target.value);
                  setResetError("");
                }}
                required
                autoComplete="new-password"
              />
              <span className="eye" onClick={() => setShowResetPass((v) => !v)}>
                {showResetPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </span>
            </PasswordInputWrap>
            <PasswordInputWrap
              $error={resetConfirm.length > 0 && resetPass !== resetConfirm}
            >
              <input
                type={showResetConfirm ? "text" : "password"}
                placeholder="Confirm Password"
                value={resetConfirm}
                onChange={(e) => {
                  setResetConfirm(e.target.value);
                  setResetError("");
                }}
                required
                autoComplete="new-password"
              />
              <span
                className="eye"
                onClick={() => setShowResetConfirm((v) => !v)}
              >
                {showResetConfirm ? (
                  <FiEyeOff size={16} />
                ) : (
                  <FiEye size={16} />
                )}
              </span>
            </PasswordInputWrap>
            <ErrorHint
              style={{ visibility: resetError ? "visible" : "hidden" }}
            >
              {resetError || "Invalid."}
            </ErrorHint>
            <button type="submit" disabled={loading}>
              {loading ? "Resetting..." : "Reset Password"}
            </button>
          </form>
          <span
            className="text-link text-link-gray"
            onClick={() => {
              navigate("/");
              setIsResetDismissed(true);
              setAuthMode("signin");
            }}
          >
            Back to Sign In
          </span>
        </div>

        <div className="reset-view reset-success-view">
          <div
            className="icon-circle"
            style={{ color: "#10b981", background: "rgba(16, 185, 129, 0.08)" }}
          >
            <FiCheckCircle size={32} />
          </div>
          <h2>Password Reset!</h2>
          <p>
            Your password has been updated successfully. You can now sign in
            with your new credentials.
          </p>
          <span
            className="text-link"
            onClick={() => {
              window.location.href = "/login";
            }}
          >
            Continue to Workspace
          </span>
        </div>
      </ResetPanel>
    </Box>
  );
}

export default AuthPanel;
